import React, { useState, MouseEvent, useMemo } from 'react'
import classNames from 'classnames'
import Sites from '@toasttab/shared/src/data/Sites.json'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconWorkplaceEarth } from '@toasttab/shared/src/assets/svg/icon-workplace-earth.svg'
import { SiteSchema } from '@toasttab/shared/src/interfaces/Site.d'
import { formatUrl } from '@toasttab/shared/src/utils/siteUtils'

export interface MainRegionSelectorProps {
  currentLanguage?: string
  footerLinkCss: any
  localizedEntries?: any[]
  packageName: string
  siteId: number
  hostName?: string
}

const RegionLanguageSelector = ({
  footerLinkCss,
  localizedEntries = [],
  packageName,
  siteId
}: MainRegionSelectorProps) => {
  const { t } = useI18next()
  const [showModal, setShowModal] = useState(false)

  const activeSite = useMemo(
    () => Sites.sites.find((site) => site.siteId === siteId),
    [siteId]
  )

  const pkg = useMemo(
    () => Sites.packages.find((p) => p.package === packageName),
    [packageName]
  )

  const modalClasses = classNames(
    'fixed bg-black bg-opacity-30 inset-0 w-full z-50 flex items-center justify-center p-4',
    { hidden: !showModal }
  )

  const getCurrentSiteName = () =>
    Sites.sites.find((site) => site.siteId === siteId)?.siteName || 'US'

  const groupedSites = useMemo(() => {
    return Sites.sites.reduce<Record<string, SiteSchema[]>>((acc, site) => {
      const entry = localizedEntries.find((e) => e.language === site.language)
      const fallback = localizedEntries.find(
        (e) => e.language === activeSite?.language
      )
      const slug =
        entry?.slugOverride ||
        entry?.slug ||
        fallback?.slugOverride ||
        fallback?.slug ||
        ''
      const url = formatUrl(site, pkg?.path || '', slug)
      acc[site.continent] = acc[site.continent] || []
      acc[site.continent].push({ ...site, url })
      return acc
    }, {})
  }, [localizedEntries, pkg?.path, activeSite?.language])

  return (
    <>
      <button
        className={`${footerLinkCss} before:content-icon-map-marker before:mr-2`}
        data-testid='MainRegionSelector-openButton'
        onClick={() => setShowModal(true)}
      >
        {t('Region:')} {getCurrentSiteName()}
      </button>

      <div
        aria-hidden={!showModal}
        className={modalClasses}
        data-testid='MainRegionSelector-modal'
        onClick={() => setShowModal(false)}
        role='dialog'
      >
        <div
          className='relative w-full max-w-[550px] max-h-[800px] bg-white rounded-lg shadow-xl overflow-y-auto'
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <button
            className='absolute top-1 right-1'
            data-testid='MainRegionSelector-closeButton'
            onClick={() => setShowModal(false)}
          >
            <CloseIcon height='30' width='30' />
          </button>

          <nav aria-label='Switch language' className='p-10'>
            <IconWorkplaceEarth height='28' width='28' />
            <h4 className='text-2xl font-normal mb-6'>
              {t('Choose your country and language:')}
            </h4>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {Object.entries(groupedSites).map(([continent, sites]) => (
                <div key={continent}>
                  <h3 className='uppercase text-sm mb-2 font-sansPro font-semibold'>
                    {continent}
                  </h3>
                  <ul>
                    {sites.map((site) => (
                      <li className='mb-2' key={`site-${site.siteId}`}>
                        <a
                          className='font-normal text-blue-75 text-base'
                          href={site.url || '#'}
                        >
                          {site.languageName}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}

export default RegionLanguageSelector
